import { CRMFieldType, Lead } from '@/api/types/crm.types'
import { FC } from 'react'
import iclasses from '../../styles/input.module.css'
import { InputTextarea } from '@UI/InputNew'
import cn from 'classnames'
import { Field } from '@/api/types/crm.types'
import dayjs from 'dayjs'
import Select from '../base/Select/Select'
import PhoneIntegrationButtons from '../PhoneIntegrationButtons/PhoneIntegrationButtons'
import classes from './index.module.css'
import Input from '../base/Input/Input'
import DatePicker from '../base/DatePicker/DatePicker'

interface FieldProps {
  id: string
  name: string
  size?: 'small' | 'middle'
  field?: Field
  value?: string
  onChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | string,
  ) => void
  error?: string
  placeholder?: string
}

export interface VariableLeadFieldSettings {
  phoneNumber?: {
    withIntegrations?:
      | {
          lead: Lead
          fieldId: number
          fieldSettings: unknown
        }
      | false
  }
}

interface VariableLeadFieldProps {
  type: CRMFieldType
  fieldProps: FieldProps
  fieldSettings?: VariableLeadFieldSettings
}

const VariableLeadField: FC<VariableLeadFieldProps> = ({
  type,
  fieldProps: { id, name, size, field, value, onChange, error, placeholder },
  fieldSettings,
}) => {
  let commonProps = {
    id: id ?? field?.unique_name,
    name: name ?? field?.unique_name,
    value,
    onChange,
    error,
    placeholder: placeholder ?? field?.name,
  }

  switch (type) {
    case 'textfield':
      return (
        <>
          <Input
            size={size ?? 'small'}
            status={commonProps.error ? 'error' : undefined}
            {...commonProps}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'number':
      return (
        <>
          <Input
            size={size ?? 'small'}
            type="number"
            status={commonProps.error ? 'error' : undefined}
            {...commonProps}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'textarea':
      return (
        // @ts-ignore
        <InputTextarea
          className={cn(
            iclasses.newTextareaInputDesign,
            iclasses.newTextareaInputDesignBorderSmall,
          )}
          validate={!!error}
          errorText={error}
          typeValid="custom"
          {...commonProps}
        />
      )
    case 'select':
      return (
        <>
          <Select
            showSearch
            allowClear
            size={size ?? 'small'}
            status={error ? 'error' : undefined}
            options={(field?.value_options ?? []).map((value) => ({
              label: value,
              value,
            }))}
            {...commonProps}
            value={commonProps.value?.length ? commonProps.value : undefined}
            onClear={() => commonProps.onChange('')}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'datetime':
      return (
        <>
          <DatePicker
            showTime
            size={size ?? 'small'}
            onChange={(_, value) => commonProps.onChange(value)}
            name={commonProps.name}
            id={commonProps.id}
            status={error ? 'error' : undefined}
            placeholder={commonProps.placeholder}
            value={commonProps.value ? dayjs(commonProps.value) : null}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'date':
      return (
        <>
          <DatePicker
            // format={'YYYY.MM.DD'}
            size={size ?? 'small'}
            onChange={(_, value) => commonProps.onChange(value)}
            name={commonProps.name}
            id={commonProps.id}
            status={error ? 'error' : undefined}
            placeholder={commonProps.placeholder}
            value={commonProps.value ? dayjs(commonProps.value) : null}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'phone_number':
      return (
        <>
          <Input
            size={size ?? 'small'}
            type="tel"
            maxLength={20}
            status={commonProps.error ? 'error' : undefined}
            suffix={
              fieldSettings?.phoneNumber?.withIntegrations && (
                <PhoneIntegrationButtons
                  leadId={
                    fieldSettings?.phoneNumber?.withIntegrations?.lead?.id
                  }
                  fieldId={
                    fieldSettings?.phoneNumber?.withIntegrations?.fieldId
                  }
                  fieldSettings={
                    fieldSettings?.phoneNumber?.withIntegrations?.fieldSettings
                  }
                />
              )
            }
            className={cn({
              [classes.inputPhoneNumberWithIntegration]:
                fieldSettings?.phoneNumber?.withIntegrations,
            })}
            {...commonProps}
            placeholder="+7 (999) 999-99-99"
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )

    default:
      return null
  }
}

export default VariableLeadField
